import { type ReactNode } from "react";

import { type Conversation } from "shared/apiClient";
import { AssessmentLights } from "shared/types";
import { getText } from "shared/lib";

import { ExternalLinkIcon } from "shared/components";

export interface MITProps {
  assessmentLights: string;
  conversation: Conversation | undefined | null;
}

type LinkItem = {
  href: string;
  text: string;
};

function NextStepLink({ href, text }: LinkItem) {
  return (
    <a
      className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
      href={href}
      rel="noreferrer"
      tabIndex={0}
      target="_blank"
    >
      {getText(text)}
      <ExternalLinkIcon className="ml-2 mt-[3px]" />
    </a>
  );
}

function NextStepItem({
  title,
  body,
  links,
  children,
}: {
  title: string;
  body: string;
  links?: LinkItem[];
  children?: ReactNode;
}) {
  return (
    <div>
      <h3 className="text-headingmd">{getText(title)}</h3>
      <p className="text-bodymd mb-2">{getText(body)}</p>
      {children}
      {!!links?.length && (
        <div className="flex flex-col items-start gap-1">
          {links.map((link) => (
            <NextStepLink key={`${link.href}-${link.text}`} {...link} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportId({ conversation }: Pick<MITProps, "conversation">) {
  return (
    <div className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodylg w-min text-nowrap rounded border px-2 py-1.5 font-medium">
      {conversation?.identifier?.toUpperCase() || ""}
    </div>
  );
}

export function MIT({ assessmentLights, conversation }: MITProps) {
  const titleLinks = [
    {
      href: "https://www.mitchellshire.vic.gov.au/plan-and-build/building/request-building-plans-or-property-information",
      text: "Request building plans or property information",
    },
    {
      href: "https://www.landata.online/",
      text: "Landata",
    },
  ];

  const appointmentLink = {
    href: "https://www.mitchellshire.vic.gov.au/plan-and-build/building/request-a-planning-or-building-appointment",
    text: "Request an appointment",
  };

  const planningAdviceLink = {
    href: "https://www.mitchellshire.vic.gov.au/plan-and-build/planning/request-a-meeting-or-get-written-advice",
    text: "Get planning advice and information",
  };

  const renderRequestCertificateOfTitle = () => {
    return (
      <NextStepItem
        title="Request a Certificate of Title"
        body="This service does not take into account covenants, restrictions, agreements, caveats or easements, which can affect what you can and cannot do on a property. A Certificate of Title and associated documents include this information and can be obtained online through Council or Landata."
        links={titleLinks}
      />
    );
  };

  const renderCheckIfYouNeedABuildingPermit = () => {
    return (
      <NextStepItem
        title="⚠️ You may still require a building permit"
        body="This service does not determine whether approval is required under the Building Regulations 2018. For more information about works that may require a building permit, please visit Council's Do I need a building permit? page. Please note that building permits may be issued by Council or a registered private building surveyor."
        links={[
          {
            href: "https://www.mitchellshire.vic.gov.au/plan-and-build/building/building-permits/do-i-need-a-building-permit",
            text: "Do I need a building permit?",
          },
        ]}
      />
    );
  };

  const renderApplyForInformationOnPlanningControls = () => {
    return (
      <NextStepItem
        title="Apply for information on planning controls"
        body="If you need formal written advice for your builder or surveyor confirming that a planning permit is not required, you can request written advice on planning controls through Council's eServices portal. You will need to log in or create an account to use this service."
        links={[planningAdviceLink]}
      />
    );
  };

  const renderDiscussYourProposalWithAPlanner = () => {
    return (
      <NextStepItem
        title="Discuss your proposal with a planner"
        body="If you have questions or would like to confirm the results of this advice, you can request a meeting to speak to one of our planners or phone 03 5734 6200. Please quote your enquiry ID from myLot."
        links={[appointmentLink]}
      >
        <ReportId conversation={conversation} />
      </NextStepItem>
    );
  };

  const renderApplyForInformationOnPlanningControlsYellow = () => {
    return (
      <NextStepItem
        title="Apply for information on planning controls"
        body="If you require formal written advice to clarify whether a planning permit is required, you can request written advice on planning controls through Council's eServices portal. You will need to log in or create an account to use this service."
        links={[planningAdviceLink]}
      />
    );
  };

  const renderSeekPreApplicationAdvice = () => {
    return (
      <NextStepItem
        title="Seek pre-application advice"
        body="For more complex proposals, pre-application advice allows you to understand constraints, considerations and Council requirements before lodging a planning permit application. This may include meetings and/or written advice. Please quote your enquiry ID from myLot."
        links={[appointmentLink, planningAdviceLink]}
      >
        <ReportId conversation={conversation} />
      </NextStepItem>
    );
  };

  const renderRefineYourProposal = () => {
    return (
      <NextStepItem
        title="Refine your proposal"
        body="We couldn't finalise your advice because some questions were unanswered or need to be reviewed. Please check your myLot report, review the unanswered questions, and start a new enquiry to receive accurate planning advice."
      />
    );
  };

  const renderDiscussYourProposalWithAPlannerBlue = () => {
    return (
      <NextStepItem
        title="Discuss your proposal with a planner"
        body="If you need help understanding how planning controls or exemptions apply to your site, you can request an appointment to discuss your proposal with a Council planner. Please quote your enquiry ID from myLot."
        links={[appointmentLink]}
      >
        <ReportId conversation={conversation} />
      </NextStepItem>
    );
  };

  const renderDiscussYourProposalWithAPlannerRed = () => {
    return (
      <NextStepItem
        title="Discuss your proposal with a planner"
        body="Based on the information provided, your proposal may not be supported under the Planning Scheme. Before proceeding further, we strongly recommend that you request an appointment to discuss your proposal with a Council planner. Please quote your enquiry ID from myLot."
        links={[appointmentLink]}
      >
        <ReportId conversation={conversation} />
      </NextStepItem>
    );
  };

  switch (assessmentLights) {
    case AssessmentLights.NoPermitRequired:
      return (
        <>
          {renderRequestCertificateOfTitle()}
          {renderCheckIfYouNeedABuildingPermit()}
          {renderApplyForInformationOnPlanningControls()}
          {renderDiscussYourProposalWithAPlanner()}
        </>
      );
    case AssessmentLights.MoreInformationRequired:
      return (
        <>
          {renderRefineYourProposal()}
          {renderSeekPreApplicationAdvice()}
          {renderDiscussYourProposalWithAPlannerBlue()}
        </>
      );
    case AssessmentLights.RequiresPermit:
      return (
        <>
          {renderRequestCertificateOfTitle()}
          {renderApplyForInformationOnPlanningControlsYellow()}
          {renderSeekPreApplicationAdvice()}
        </>
      );
    case AssessmentLights.Prohibited:
      return <>{renderDiscussYourProposalWithAPlannerRed()}</>;
  }
}
